"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAssistantQuickActions = void 0;
const store_1 = require("@app/store");
const _ui_1 = require("@ui");
const util_1 = require("@app/util");
// An icon for every line in the DELIVERED CATALOGUE, keyed by the line's own Tolgee key.
//
// This is a LOOKUP, not inference. Workspaces configure their opening lines by pointing at
// these very keys — DEV's `test WS michal SSO` configures nine of them — so a configured line
// can be given the same glyph the built-in set would have given it, without anything having to
// guess an icon from the line's text. A line that is genuinely custom is not in here and keeps
// `FALLBACK_ICON`.
//
// Without this, a configured strip rendered N copies of one speech bubble where the design
// draws distinct glyphs. The alternative on offer was to discard the whole configuration and
// show the built-in set instead, which silently threw away shortcuts a business had set up.
//
// Icons are existing assets from `Icon.ICONS`; nothing was drawn for this. Two obvious picks
// had to be rejected after looking at them at 12px: `assistant.svg` carries `fill="none"` with
// no `fill-primary` class and renders as nothing, and `capacity.svg` opens with a full-bounds
// filled rect and renders as a black square. Anything added here needs the same look — a
// `fill-primary` path on an 18x18 viewBox, like the rest of these.
const CATALOGUE_ICONS = {
    'ASSISTANT.openingLinesSection1.line1': _ui_1.Icon.ICONS.duplicate,
    'ASSISTANT.openingLinesSection1.line2': _ui_1.Icon.ICONS.shift,
    'ASSISTANT.openingLinesSection1.line3': _ui_1.Icon.ICONS.tip,
    'ASSISTANT.openingLinesSection2.line1': _ui_1.Icon.ICONS.absence,
    'ASSISTANT.openingLinesSection2.line2': _ui_1.Icon.ICONS.edit,
    'ASSISTANT.openingLinesSection2.line3': _ui_1.Icon.ICONS.vacation,
    'ASSISTANT.openingLinesSection3.line1': _ui_1.Icon.ICONS.hoursDivision,
    'ASSISTANT.openingLinesSection3.line2': _ui_1.Icon.ICONS.clock,
    'ASSISTANT.openingLinesSection4.line1': _ui_1.Icon.ICONS.work,
};
// The built-in set, for a workspace that has configured nothing: the design's eight, which is
// the catalogue minus `openingLinesSection4.line1` — nine would not fit the strip the design
// draws. Key names, not text: the configuration stores keys and no user-facing literal belongs
// in code.
const DEFAULT_KEYS = [
    'ASSISTANT.openingLinesSection1.line1',
    'ASSISTANT.openingLinesSection1.line2',
    'ASSISTANT.openingLinesSection1.line3',
    'ASSISTANT.openingLinesSection2.line1',
    'ASSISTANT.openingLinesSection2.line2',
    'ASSISTANT.openingLinesSection2.line3',
    'ASSISTANT.openingLinesSection3.line1',
    'ASSISTANT.openingLinesSection3.line2',
];
// What a line outside the catalogue shows — the `chat` glyph the starter cards used before the
// strip replaced them. One honest default beats inferring an icon from a line's wording.
const FALLBACK_ICON = _ui_1.Icon.ICONS.chat;
// A configured line stores `<key>.userTitle`, so the key it came from is that minus the field.
// Any shape that is not a catalogue key simply misses the lookup and takes the fallback.
const iconForLine = (line) => CATALOGUE_ICONS[String(line?.userTitle).replace(/\.userTitle$/, '')] ?? FALLBACK_ICON;
const toQuickAction = (line, ico) => {
    const label = (0, util_1.t)(line?.userTitle);
    const prompt = (0, util_1.t)(line?.botPrompt);
    // A line with nothing to insert is not a shortcut. `t()` is typed `string | null` here and
    // the configuration is untyped JSON, so this is a real case, not defensive noise.
    if (!(0, util_1.isValue)(label) || !(0, util_1.isValue)(prompt))
        return null;
    return { id: line.userTitle, label, prompt, ico };
};
/**
 * The single definition of the sidebar's quick-action chip set. The chat renders all of them
 * above the composer; the collapsed rail renders the same list as icons, as many as it has room
 * for. Two components, one list: a rail that showed different shortcuts from the strip would be
 * a bug nobody notices until a manager expands the sidebar.
 *
 * A workspace's own configured opening lines win whenever it has any — flat first, then any
 * sectioned line the flat list does not already carry. `DEFAULT_KEYS` is what a workspace that
 * has configured nothing gets. The de-duplication is not decoration: `userTitle` is the React
 * key, and the same line appearing in both fields would render two chips with one id.
 *
 * Both paths take their icon from the same `CATALOGUE_ICONS` lookup, so a configured chip and a
 * built-in chip pointing at the same catalogue line cannot drift apart.
 */
const useAssistantQuickActions = (workspaceId) => {
    const ws = (0, store_1.useRootSelector)((state) => state.workspaces).find((w) => w.id === workspaceId);
    const flatLines = ws?.settings?.plannerDefaults?.assistantConversationOpeningLines ?? [];
    const sections = ws?.settings?.plannerDefaults?.assistantConversationOpeningLinesStructured ?? [];
    const seen = new Set(flatLines.map((line) => line?.userTitle));
    const sectionedLines = sections
        .flatMap((section) => section?.linesInSection ?? [])
        .filter((line) => {
        if (!(0, util_1.isValue)(line?.userTitle) || seen.has(line.userTitle))
            return false;
        seen.add(line.userTitle);
        return true;
    });
    const configured = [...flatLines, ...sectionedLines];
    if (configured.length > 0) {
        return configured.map((line) => toQuickAction(line, iconForLine(line))).filter(util_1.isValue);
    }
    return DEFAULT_KEYS.map((key) => {
        const line = {
            userTitle: `${key}.userTitle`,
            userDescription: `${key}.userDescription`,
            botPrompt: `${key}.botPrompt`,
        };
        // The same lookup the configured path uses, so a built-in chip and a configured chip
        // pointing at the same catalogue line cannot end up with different glyphs.
        return toQuickAction(line, iconForLine(line));
    }).filter(util_1.isValue);
};
exports.useAssistantQuickActions = useAssistantQuickActions;
exports.default = exports.useAssistantQuickActions;
